/**
 * AlertsTable
 * Table showing active alerts with severity indicators
 */

import { AlertTriangle, AlertCircle, Info, MapPin, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/core/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/core/components/ui/table';
import { Skeleton } from '@/core/components/ui/skeleton';
import { Alert, AlertSeverity, AlertType } from '../../types/dashboard-types';
import { cn } from '@/core/lib/utils';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';

interface AlertsTableProps {
  alerts: Alert[];
  totalCount: number;
  isLoading?: boolean;
  error?: string | null;
}

function getSeverityConfig(severity: AlertSeverity) {
  switch (severity) {
    case 'Critical':
      return {
        icon: <AlertCircle className="h-4 w-4" />,
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        borderColor: 'border-l-red-600',
      };
    case 'Warning':
      return {
        icon: <AlertTriangle className="h-4 w-4" />,
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-700',
        borderColor: 'border-l-yellow-500',
      };
    case 'Info':
    default:
      return {
        icon: <Info className="h-4 w-4" />,
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        borderColor: 'border-l-blue-600',
      };
  }
}

function getTypeBadgeColor(type: AlertType): string {
  switch (type) {
    case 'Connectivity':
      return 'bg-purple-100 text-purple-700';
    case 'Calculation':
      return 'bg-cyan-100 text-cyan-700';
    case 'Payment':
      return 'bg-pink-100 text-pink-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

function formatTimeAgo(date: Date): string {
  // Guard against invalid dates
  if (!date || isNaN(date.getTime())) {
    return 'Unknown';
  }

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else {
    return `${diffDays}d ago`;
  }
}

function SeverityBadge({ severity }: { severity: AlertSeverity }) {
  const config = getSeverityConfig(severity);
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
        config.bgColor,
        config.textColor
      )}
    >
      {config.icon}
      {severity}
    </div>
  );
}

function TableSkeleton() {
  return (
    <TableBody>
      {Array.from({ length: 5 }).map((_, i) => (
        <TableRow key={i}>
          <TableCell><Skeleton className="h-6 w-20" /></TableCell>
          <TableCell><Skeleton className="h-4 w-16" /></TableCell>
          <TableCell><Skeleton className="h-4 w-48" /></TableCell>
          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
          <TableCell><Skeleton className="h-4 w-16" /></TableCell>
        </TableRow>
      ))}
    </TableBody>
  );
}

export function AlertsTable({ alerts, totalCount, isLoading = false, error }: AlertsTableProps) {
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <p className="font-medium">Error loading alerts</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="border-b">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertTriangle className="h-5 w-5 text-gray-500" />
            Active Alerts
          </CardTitle>
          <span className="text-sm text-gray-500">
            {isLoading ? '...' : `${totalCount} alert${totalCount !== 1 ? 's' : ''}`}
          </span>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="scrollbar-visible">
          <Table>
            <TableHeader className={GRID_STYLES.header.background}>
              <TableRow className={cn(GRID_STYLES.header.background, 'border-b', GRID_STYLES.border)}>
                <TableHead className={cn('w-[100px]', GRID_STYLES.header.text)}>Severity</TableHead>
                <TableHead className={cn('w-[100px]', GRID_STYLES.header.text)}>Type</TableHead>
                <TableHead className={GRID_STYLES.header.text}>Alert</TableHead>
                <TableHead className={GRID_STYLES.header.text}>Location</TableHead>
                <TableHead className={cn('w-[100px]', GRID_STYLES.header.text)}>Time</TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <TableSkeleton />
            ) : alerts.length === 0 ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={5} className="h-32 text-center text-gray-500">
                    <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>No alerts found</p>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {alerts.map((alert) => {
                  const severityConfig = getSeverityConfig(alert.severity);
                  return (
                    <TableRow
                      key={alert.alertId}
                      className={cn('border-l-4', severityConfig.borderColor, GRID_STYLES.row.hover)}
                    >
                      <TableCell>
                        <SeverityBadge severity={alert.severity} />
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            'inline-block px-2 py-1 rounded text-xs font-medium',
                            getTypeBadgeColor(alert.type)
                          )}
                        >
                          {alert.type}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-gray-900">{alert.title}</p>
                          <p className="text-sm text-gray-500 mt-0.5">{alert.description}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {alert.machineName ? (
                          <div className="flex items-start gap-1.5">
                            <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                            <div>
                              <p className="font-medium text-gray-900">{alert.machineName}</p>
                              <p className="text-sm text-gray-500">{alert.locationName}</p>
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Clock className="h-3.5 w-3.5" />
                          {formatTimeAgo(alert.createdAt)}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
